// EmailSettingsPanel — admin-only Settings sub-panel to manage the mail
// provider (Resend / SendGrid), from-address, credentials, live test, and
// Resend domain verification (SPF/DKIM records).
//
// Every request goes through the authenticated axios instance from
// AuthContext so it inherits the X-BU-Context header and Bearer token.
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Mail, Send, RefreshCw, Trash2, Plus, ShieldCheck, Copy, Globe2 } from 'lucide-react';

const REGION_OPTIONS = [
  { value: 'us-east-1',      label: 'US East (N. Virginia)' },
  { value: 'eu-west-1',      label: 'Europe (Ireland)' },
  { value: 'sa-east-1',      label: 'South America (São Paulo)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
];

const DomainRecordRow = ({ record }) => (
  <div className="rounded-md border border-slate-800 bg-slate-950 p-3 flex flex-col gap-1.5 text-xs">
    <div className="flex items-center gap-2 flex-wrap">
      <Badge variant="outline" className="border-cyan-800 text-cyan-300 bg-cyan-950/40">
        {record.record || record.type}
      </Badge>
      <span className="text-slate-400 uppercase tracking-wide">{record.type}</span>
      {record.priority ? (
        <span className="text-slate-500">priority {record.priority}</span>
      ) : null}
      <Badge variant="outline" className={
        (record.status === 'verified')
          ? 'border-emerald-800 text-emerald-300 bg-emerald-950/40'
          : 'border-amber-800 text-amber-300 bg-amber-950/40'
      }>
        {record.status || 'pending'}
      </Badge>
    </div>
    <div className="flex items-start gap-2 text-slate-200">
      <span className="text-slate-500 w-14 shrink-0">host</span>
      <code className="font-mono text-[11px] break-all">{record.name}</code>
      <Button size="sm" variant="ghost" className="h-6 px-2"
        onClick={() => { navigator.clipboard.writeText(record.name); toast.success('Host copied'); }}
        data-testid="dns-copy-host">
        <Copy className="w-3 h-3" />
      </Button>
    </div>
    <div className="flex items-start gap-2 text-slate-200">
      <span className="text-slate-500 w-14 shrink-0">value</span>
      <code className="font-mono text-[11px] break-all">{record.value}</code>
      <Button size="sm" variant="ghost" className="h-6 px-2"
        onClick={() => { navigator.clipboard.writeText(record.value); toast.success('Value copied'); }}
        data-testid="dns-copy-value">
        <Copy className="w-3 h-3" />
      </Button>
    </div>
  </div>
);

const DomainCard = ({ domain, onVerify, onDelete, verifying }) => {
  const state = (domain.status || 'not_started').toLowerCase();
  const badgeCls = state === 'verified'
    ? 'border-emerald-800 text-emerald-300 bg-emerald-950/40'
    : state === 'pending' || state === 'verifying' || state === 'in_progress'
      ? 'border-amber-800 text-amber-300 bg-amber-950/40'
      : 'border-slate-700 text-slate-300 bg-slate-800/40';

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4 space-y-3"
         data-testid={`domain-${domain.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Globe2 className="w-4 h-4 text-cyan-400 shrink-0" />
          <span className="text-slate-100 font-medium truncate">{domain.name}</span>
          <Badge variant="outline" className={badgeCls}>{state.replace(/_/g, ' ')}</Badge>
          {domain.region ? (
            <span className="text-[10px] uppercase text-slate-500 tracking-wide">{domain.region}</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline"
            className="border-slate-700 text-slate-200 hover:bg-slate-800"
            disabled={verifying}
            onClick={() => onVerify(domain.id)}
            data-testid="verify-domain-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${verifying ? 'animate-spin' : ''}`} />
            Check DNS
          </Button>
          <Button size="sm" variant="outline"
            className="border-red-900 text-red-300 hover:bg-red-950/40"
            onClick={() => onDelete(domain.id)}
            data-testid="delete-domain-btn">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      {domain.records?.length ? (
        <div className="space-y-2">
          {domain.records.map((rec, i) => <DomainRecordRow record={rec} key={i} />)}
        </div>
      ) : (
        <div className="text-slate-500 text-xs">No DNS records returned — click "Check DNS" to refresh.</div>
      )}
    </div>
  );
};

export default function EmailSettingsPanel() {
  const { api } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    provider: 'sendgrid',
    from_email: '',
    from_name: '',
    resend_api_key: '',
    sendgrid_api_key: '',
  });
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  // Domain state (only relevant when provider = resend)
  const [domains, setDomains] = useState([]);
  const [domainLoading, setDomainLoading] = useState(false);
  const [newDomainName, setNewDomainName] = useState('');
  const [newDomainRegion, setNewDomainRegion] = useState('us-east-1');
  const [creatingDomain, setCreatingDomain] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/email/status');
      setStatus(data);
      setForm((prev) => ({
        ...prev,
        provider: data.provider || 'sendgrid',
        from_email: data.from_email || '',
        from_name: data.from_name || '',
      }));
    } catch (e) {
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadDomains = useCallback(async () => {
    setDomainLoading(true);
    try {
      const { data } = await api.get('/admin/email/domains');
      setDomains(data.domains || []);
    } catch (e) {
      // Silent if resend key not set — the UI already flags it
      setDomains([]);
    } finally {
      setDomainLoading(false);
    }
  }, [api]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => {
    if (status?.provider === 'resend' && status?.has_resend_key) loadDomains();
  }, [status, loadDomains]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        provider: form.provider,
        from_email: form.from_email || undefined,
        from_name: form.from_name || undefined,
      };
      // Only send keys the admin actually typed (avoid overwriting with blanks)
      if (form.resend_api_key.trim())    payload.resend_api_key = form.resend_api_key.trim();
      if (form.sendgrid_api_key.trim())  payload.sendgrid_api_key = form.sendgrid_api_key.trim();
      const { data } = await api.put('/admin/email/settings', payload);
      setStatus((prev) => ({ ...(prev || {}), ...data }));
      setForm((prev) => ({ ...prev, resend_api_key: '', sendgrid_api_key: '' }));
      toast.success('Email settings saved');
      // Reload domains if provider is now resend
      if (data.provider === 'resend') loadDomains();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error('Enter a recipient email'); return; }
    setTesting(true);
    try {
      const { data } = await api.post('/admin/email/test', { to: testTo.trim() });
      toast.success(`Test sent via ${data.provider}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally {
      setTesting(false);
    }
  };

  const addDomain = async () => {
    if (!newDomainName.trim()) { toast.error('Enter a domain name'); return; }
    setCreatingDomain(true);
    try {
      const { data } = await api.post('/admin/email/domains', {
        name: newDomainName.trim().toLowerCase(),
        region: newDomainRegion,
      });
      toast.success(`Registered ${data.name} — add the DNS records below`);
      setNewDomainName('');
      loadDomains();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Domain registration failed');
    } finally {
      setCreatingDomain(false);
    }
  };

  const verifyDomain = async (id) => {
    setVerifyingId(id);
    try {
      const { data } = await api.get(`/admin/email/domains/${id}/verify`);
      toast.success(`Domain state: ${data.status || 'checked'}`);
      loadDomains();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification check failed');
    } finally {
      setVerifyingId(null);
    }
  };

  const deleteDomain = async (id) => {
    if (!window.confirm('Remove this domain from Resend?')) return;
    try {
      await api.delete(`/admin/email/domains/${id}`);
      toast.success('Domain removed');
      loadDomains();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const providerBadge = (
    <Badge variant="outline" className={
      status?.enabled
        ? 'border-emerald-800 text-emerald-300 bg-emerald-950/40'
        : 'border-red-900 text-red-300 bg-red-950/40'
    }>
      {status?.enabled ? 'active' : 'not configured'}
    </Badge>
  );

  return (
    <div className="space-y-6" data-testid="email-settings-panel">
      {/* Provider + credentials */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-cyan-400" /> Email Provider
            {status ? providerBadge : null}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Provider</Label>
              <Select
                value={form.provider}
                onValueChange={(v) => setForm((p) => ({ ...p, provider: v }))}
              >
                <SelectTrigger className="bg-slate-950 border-slate-800 mt-1.5"
                  data-testid="email-provider-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="resend">Resend</SelectItem>
                  <SelectItem value="sendgrid">SendGrid</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-slate-500 text-xs mt-1.5">
                Both providers share the same code path — flipping this changes every outbound email instantly.
              </p>
            </div>
            <div>
              <Label className="text-slate-300">From name</Label>
              <Input
                value={form.from_name}
                onChange={(e) => setForm((p) => ({ ...p, from_name: e.target.value }))}
                className="bg-slate-950 border-slate-800 mt-1.5"
                placeholder="AeroGuard SMS"
                data-testid="email-from-name-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-slate-300">From email</Label>
              <Input
                value={form.from_email}
                onChange={(e) => setForm((p) => ({ ...p, from_email: e.target.value }))}
                className="bg-slate-950 border-slate-800 mt-1.5 font-mono text-sm"
                placeholder="notifications@aeroguard.sa"
                data-testid="email-from-email-input"
              />
              <p className="text-slate-500 text-xs mt-1.5">
                Must belong to a domain you've verified in the provider — {' '}
                <code className="text-cyan-300">onboarding@resend.dev</code> works instantly for testing.
              </p>
            </div>
            <div>
              <Label className="text-slate-300">Resend API key</Label>
              <Input
                type="password"
                value={form.resend_api_key}
                onChange={(e) => setForm((p) => ({ ...p, resend_api_key: e.target.value }))}
                className="bg-slate-950 border-slate-800 mt-1.5 font-mono text-sm"
                placeholder={status?.has_resend_key ? '••••••••••••••• (already set)' : 're_...'}
                data-testid="email-resend-key-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">SendGrid API key</Label>
              <Input
                type="password"
                value={form.sendgrid_api_key}
                onChange={(e) => setForm((p) => ({ ...p, sendgrid_api_key: e.target.value }))}
                className="bg-slate-950 border-slate-800 mt-1.5 font-mono text-sm"
                placeholder="SG...."
                data-testid="email-sendgrid-key-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
            <Button onClick={loadStatus} disabled={loading} variant="ghost"
              className="text-slate-400 hover:text-slate-200"
              data-testid="email-refresh-status-btn">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={save} disabled={saving}
              className="bg-cyan-600 hover:bg-cyan-500 text-white"
              data-testid="email-save-settings-btn">
              {saving ? 'Saving…' : 'Save settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Test send */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Send className="w-5 h-5 text-cyan-400" /> Send a test email
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col md:flex-row gap-2">
            <Input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              className="bg-slate-950 border-slate-800 md:flex-1"
              placeholder="recipient@example.com"
              data-testid="email-test-to-input"
            />
            <Button onClick={sendTest} disabled={testing || !status?.enabled}
              className="bg-cyan-600 hover:bg-cyan-500 text-white"
              data-testid="email-send-test-btn">
              {testing ? 'Sending…' : 'Send test'}
            </Button>
          </div>
          <p className="text-slate-500 text-xs">
            The message uses your current provider + from address. Resend's sandbox domain
            (<code className="text-cyan-300">onboarding@resend.dev</code>) only delivers to the
            account owner's email until a real domain is verified.
          </p>
        </CardContent>
      </Card>

      {/* Domain verification (Resend only) */}
      {form.provider === 'resend' && (
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-cyan-400" /> Domain verification
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!status?.has_resend_key && (
              <div className="rounded-md border border-amber-900 bg-amber-950/30 text-amber-200 text-sm p-3">
                Add a Resend API key above and save to manage domains.
              </div>
            )}
            <div className="flex flex-col md:flex-row gap-2">
              <Input
                value={newDomainName}
                onChange={(e) => setNewDomainName(e.target.value)}
                className="bg-slate-950 border-slate-800 md:flex-1"
                placeholder="aeroguard.sa"
                data-testid="new-domain-name-input"
              />
              <Select value={newDomainRegion} onValueChange={setNewDomainRegion}>
                <SelectTrigger className="bg-slate-950 border-slate-800 md:w-[240px]"
                  data-testid="new-domain-region-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {REGION_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={addDomain} disabled={creatingDomain || !status?.has_resend_key}
                className="bg-cyan-600 hover:bg-cyan-500 text-white"
                data-testid="add-domain-btn">
                <Plus className="w-4 h-4 mr-1.5" />
                {creatingDomain ? 'Adding…' : 'Add domain'}
              </Button>
            </div>

            {domainLoading && <div className="text-slate-500 text-sm">Loading domains…</div>}
            {!domainLoading && domains.length === 0 && (
              <div className="text-slate-500 text-sm">
                No domains registered yet. Add <code className="text-cyan-300">aeroguard.sa</code> to unlock
                sending to any recipient (not just the Resend account owner).
              </div>
            )}
            <div className="space-y-3">
              {domains.map((d) => (
                <DomainCard
                  key={d.id}
                  domain={d}
                  onVerify={verifyDomain}
                  onDelete={deleteDomain}
                  verifying={verifyingId === d.id}
                />
              ))}
            </div>
            <p className="text-slate-500 text-xs">
              Copy each DNS record into your domain registrar (Cloudflare, Route53, Namecheap, …),
              wait a few minutes for propagation, then click <em>Check DNS</em>. Once verified,
              change the "From email" above to <code className="text-cyan-300">notifications@your-domain.tld</code>.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
